import React, { Component, ErrorInfo, ReactNode } from 'react';
import { captureError } from '../services/errorMonitoringService';

interface Props {
  children: ReactNode;
  /** Custom fallback UI. When provided, replaces the built-in error screen. */
  fallback?: ReactNode;
  /**
   * When set, indicates this boundary wraps a single module/page rather than
   * the whole app. The fallback will render inline (not full-screen) so the
   * sidebar and shell remain accessible.
   */
  moduleName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    captureError(error, {
      severity: this.props.moduleName ? 'error' : 'fatal',
      category: 'react_error_boundary',
      component: errorInfo.componentStack?.slice(0, 500) || undefined,
    });
    this.setState({ error, errorInfo });
  }

  /** Reset boundary state so the module can retry without reloading the page. */
  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  /** Hard-reload as last resort from the app-level boundary. */
  handleHardReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback takes priority
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorInfo } = this.state;
      const { moduleName } = this.props;

      // ── Compact inline fallback for module-level boundaries ─────────────
      if (moduleName) {
        return (
          <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-8">
            <div className="max-w-md w-full bg-white dark:bg-neutral-900 border border-red-200 dark:border-red-800 rounded p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-red-500 text-lg" aria-hidden>⚠</span>
                <h2 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                  Módulo <code className="font-mono bg-gray-100 dark:bg-neutral-800 px-1 rounded">{moduleName}</code> encontrou um erro
                </h2>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Este módulo não pôde ser carregado. Os demais módulos continuam funcionando.
              </p>
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-100 dark:border-red-900 rounded text-xs font-mono text-red-700 dark:text-red-300 break-all">
                  {error.message}
                </div>
              )}
              {errorInfo && (
                <details className="mb-4">
                  <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                    Stack trace
                  </summary>
                  <pre className="mt-2 p-3 bg-gray-100 dark:bg-neutral-800 rounded text-xs overflow-auto max-h-48 text-gray-600 dark:text-gray-400">
                    {errorInfo.componentStack}
                  </pre>
                </details>
              )}
              <div className="flex gap-2">
                <button
                  onClick={this.handleReset}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Tentar novamente
                </button>
                <a
                  href="/#/home"
                  className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-neutral-700 transition-colors"
                >
                  Ir para Home
                </a>
              </div>
            </div>
          </div>
        );
      }

      // ── Full-screen fallback for app-level boundary ──────────────────────
      return (
        <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-neutral-950">
          <div className="max-w-2xl p-8 bg-white dark:bg-neutral-900 rounded shadow-lg">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Erro na Aplicação</h1>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Ocorreu um erro inesperado. Por favor, tente recarregar a página.
            </p>
            {error && (
              <div className="mb-4 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded">
                <p className="font-mono text-sm text-red-800 dark:text-red-300">{error.toString()}</p>
              </div>
            )}
            {errorInfo && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800">
                  Detalhes técnicos
                </summary>
                <pre className="mt-2 p-4 bg-gray-100 dark:bg-neutral-800 rounded text-xs overflow-auto max-h-96">
                  {errorInfo.componentStack}
                </pre>
              </details>
            )}
            <div className="flex gap-4">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Recarregar Página
              </button>
              <button
                onClick={this.handleHardReset}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Voltar para Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
